import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL, ORIGEN_API } from "../services/api";
import {
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiMinus,
  FiPlus,
  FiX,
} from "react-icons/fi";
import "./SesionEntreno.css";

/*
 * Pantalla de entrenamiento: un ejercicio cada vez, series que se marcan
 * según se hacen y cronómetro de descanso automático.
 *
 * Todo se guarda en el servidor en cuanto se toca, así que si se cierra la
 * app a mitad del entreno no se pierde nada: al volver, se retoma.
 */

// "90s", "1:30", "2 min" → segundos. Si no se entiende, 90.
function segundosDescanso(texto) {
  if (!texto) return 90;
  const t = String(texto).toLowerCase().trim();
  const reloj = t.match(/^(\d+):(\d{1,2})$/);
  if (reloj) return Number(reloj[1]) * 60 + Number(reloj[2]);
  const min = t.match(/([\d.]+)\s*(min|m)\b/);
  if (min) return Math.round(parseFloat(min[1]) * 60);
  const seg = t.match(/([\d.]+)\s*(seg|s)\b/);
  if (seg) return Math.round(parseFloat(seg[1]));
  const solo = t.match(/^[\d.]+$/);
  if (solo) return Math.round(parseFloat(solo[0]));
  return 90;
}

const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, s % 60)).padStart(2, "0")}`;

/* ─── Cronómetro de descanso ─── */
function Descanso({ segundos, onCerrar }) {
  const [quedan, setQuedan] = useState(segundos);
  const avisado = useRef(false);

  useEffect(() => {
    setQuedan(segundos);
    avisado.current = false;
  }, [segundos]);

  useEffect(() => {
    if (quedan <= 0) {
      if (!avisado.current) {
        avisado.current = true;
        // Vibración corta al acabar: en el gimnasio no se mira la pantalla
        if (navigator.vibrate) navigator.vibrate([180, 90, 180]);
      }
      return;
    }
    const id = setTimeout(() => setQuedan((q) => q - 1), 1000);
    return () => clearTimeout(id);
  }, [quedan]);

  const pct = segundos > 0 ? Math.max(0, Math.min(1, quedan / segundos)) : 0;
  const fin = quedan <= 0;

  return (
    <div className={`sx-descanso${fin ? " sx-descanso-fin" : ""}`} role="timer" aria-live="polite">
      <div className="sx-descanso-barra" style={{ transform: `scaleX(${pct})` }} />
      <div className="sx-descanso-cuerpo">
        <span className="sx-descanso-eti">{fin ? "Descanso terminado" : "Descansando"}</span>
        <strong className="sx-descanso-reloj">{mmss(Math.max(0, quedan))}</strong>
        <div className="sx-descanso-botones">
          <button type="button" className="sx-mini" onClick={() => setQuedan((q) => q + 30)}>
            +30s
          </button>
          <button type="button" className="sx-mini sx-mini-fuerte" onClick={onCerrar}>
            {fin ? "Seguir" : "Saltar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Una serie ─── */
function Serie({ n, serie, ultima, onGuardar }) {
  const [peso, setPeso] = useState(serie.peso ?? "");
  const [reps, setReps] = useState(serie.repeticiones ?? "");

  useEffect(() => {
    setPeso(serie.peso ?? "");
    setReps(serie.repeticiones ?? "");
  }, [serie.peso, serie.repeticiones]);

  // Al marcar sin haber escrito nada, se rellena con la última marca:
  // el caso normal es repetir lo del día anterior.
  const marcar = () => {
    const p = peso !== "" ? Number(peso) : ultima?.peso ?? undefined;
    const r =
      reps !== ""
        ? Number(reps)
        : ultima?.repeticiones ?? (Number(serie.objetivoReps) || undefined);
    if (p !== undefined) setPeso(p);
    if (r !== undefined) setReps(r);
    onGuardar({ peso: p, repeticiones: r, completada: !serie.completada });
  };

  return (
    <li className={`sx-serie${serie.completada ? " sx-serie-ok" : ""}`}>
      <span className="sx-serie-n">{n}</span>

      <label className="sx-campo">
        <span>kg</span>
        <input
          type="number"
          inputMode="decimal"
          step="0.5"
          min="0"
          value={peso}
          placeholder={ultima?.peso != null ? String(ultima.peso) : "—"}
          onChange={(e) => setPeso(e.target.value)}
          onBlur={() => peso !== "" && onGuardar({ peso: Number(peso) })}
        />
      </label>

      <label className="sx-campo">
        <span>reps</span>
        <input
          type="number"
          inputMode="numeric"
          min="0"
          value={reps}
          placeholder={serie.objetivoReps || (ultima?.repeticiones != null ? String(ultima.repeticiones) : "—")}
          onChange={(e) => setReps(e.target.value)}
          onBlur={() => reps !== "" && onGuardar({ repeticiones: Number(reps) })}
        />
      </label>

      <button
        type="button"
        className="sx-check"
        onClick={marcar}
        aria-pressed={serie.completada}
        aria-label={serie.completada ? `Deshacer serie ${n}` : `Marcar serie ${n} como hecha`}
      >
        <FiCheck />
      </button>
    </li>
  );
}

/* ─── Pantalla ─── */
function SesionEntreno({ log: logInicial, onTerminar, onSalir }) {
  const [log, setLog] = useState(logInicial);
  const [i, setI] = useState(0);
  const [marcas, setMarcas] = useState({});
  const [descanso, setDescanso] = useState(null);
  const [cerrando, setCerrando] = useState(false);
  const [sensacion, setSensacion] = useState("");
  const [notas, setNotas] = useState("");
  const [error, setError] = useState("");
  const [transcurrido, setTranscurrido] = useState(0);

  const ejercicio = log.ejercicios[i];
  const total = log.ejercicios.length;

  // Reloj de sesión
  useEffect(() => {
    const desde = new Date(log.empezadoEn).getTime();
    const tic = () => setTranscurrido(Math.floor((Date.now() - desde) / 1000));
    tic();
    const id = setInterval(tic, 1000);
    return () => clearInterval(id);
  }, [log.empezadoEn]);

  // Últimas marcas de todos los ejercicios de golpe, una sola llamada
  useEffect(() => {
    (async () => {
      try {
        const { token } = await getSession();
        const nombres = log.ejercicios.map((e) => e.nombre).join(",");
        const res = await axios.get(`${API_URL}/workouts/ultimas-marcas`, {
          params: { nombres },
          headers: { Authorization: `Bearer ${token}` },
        });
        setMarcas(res.data || {});
      } catch {
        // Sin histórico se entrena igual: los campos salen vacíos
      }
    })();
  }, [log.ejercicios]);

  const guardarSerie = useCallback(
    async (k, cambios) => {
      // Optimista: la interfaz responde al instante, el servidor va detrás
      setLog((prev) => {
        const copia = structuredClone(prev);
        Object.assign(copia.ejercicios[i].series[k], cambios);
        return copia;
      });

      if (cambios.completada) {
        setDescanso(segundosDescanso(ejercicio.descanso));
      }

      try {
        const { token } = await getSession();
        const res = await axios.patch(
          `${API_URL}/workouts/${log._id}/serie`,
          { ejercicio: i, serie: k, ...cambios },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setLog((prev) => ({
          ...prev,
          volumenKg: res.data.volumenKg,
          seriesCompletadas: res.data.seriesCompletadas,
        }));
        setError("");
      } catch {
        setError("No se pudo guardar. Seguimos y lo reintentamos al cerrar.");
      }
    },
    [i, log._id, ejercicio]
  );

  const cambiarSeries = async (accion) => {
    try {
      const { token } = await getSession();
      const res = await axios.patch(
        `${API_URL}/workouts/${log._id}/ejercicio`,
        { ejercicio: i, accion },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLog((prev) => {
        const copia = structuredClone(prev);
        copia.ejercicios[i] = res.data;
        return copia;
      });
    } catch {
      setError("No se pudo cambiar el número de series.");
    }
  };

  const terminar = async () => {
    try {
      const { token } = await getSession();
      const res = await axios.post(
        `${API_URL}/workouts/${log._id}/terminar`,
        { sensacion: sensacion || undefined, notas: notas || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onTerminar(res.data);
    } catch {
      setError("No se pudo cerrar el entrenamiento. Inténtalo otra vez.");
    }
  };

  const hechasEj = ejercicio.series.filter((s) => s.completada).length;
  const hechasTotal = log.ejercicios.reduce(
    (t, e) => t + e.series.filter((s) => s.completada).length,
    0
  );
  const seriesTotal = log.ejercicios.reduce((t, e) => t + e.series.length, 0);
  const marca = marcas[ejercicio.nombre];

  /* Resumen de cierre */
  if (cerrando) {
    return (
      <div className="sx">
        <div className="sx-cierre">
          <h2>¿Cerramos el entrenamiento?</h2>
          <div className="sx-cierre-cifras">
            <div>
              <strong>{mmss(transcurrido)}</strong>
              <span>duración</span>
            </div>
            <div>
              <strong>{hechasTotal}</strong>
              <span>series</span>
            </div>
            <div>
              <strong>{(log.volumenKg || 0).toLocaleString("es-ES")}</strong>
              <span>kg movidos</span>
            </div>
          </div>

          <p className="sx-cierre-eti">¿Cómo te has encontrado?</p>
          <div className="sx-sensacion">
            {[
              ["mal", "Flojo"],
              ["normal", "Normal"],
              ["bien", "Fuerte"],
            ].map(([v, t]) => (
              <button
                key={v}
                type="button"
                className={sensacion === v ? "sx-sens-on" : ""}
                onClick={() => setSensacion(v)}
              >
                {t}
              </button>
            ))}
          </div>

          <textarea
            className="sx-notas"
            rows={3}
            placeholder="Notas para tu entrenador (opcional)"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
          />

          {error && <p className="sx-error">{error}</p>}

          <div className="sx-cierre-botones">
            <button type="button" className="sx-btn-plano" onClick={() => setCerrando(false)}>
              Seguir entrenando
            </button>
            <button type="button" className="sx-btn" onClick={terminar}>
              Terminar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sx">
      {/* Barra superior */}
      <header className="sx-top">
        <button type="button" className="sx-salir" onClick={onSalir} aria-label="Salir sin cerrar">
          <FiX />
        </button>
        <div className="sx-top-info">
          <span className="sx-top-sesion">{log.sesionNombre}</span>
          <span className="sx-top-reloj">{mmss(transcurrido)}</span>
        </div>
        <button type="button" className="sx-acabar" onClick={() => setCerrando(true)}>
          Terminar
        </button>
      </header>

      <div className="sx-progreso">
        <div
          className="sx-progreso-relleno"
          style={{ width: `${seriesTotal ? (hechasTotal / seriesTotal) * 100 : 0}%` }}
        />
      </div>

      {/* Ejercicio */}
      <main className="sx-main">
        <div className="sx-ej-cabecera">
          <span className="sx-ej-cuenta">
            Ejercicio {i + 1} de {total}
          </span>
          <h2 className="sx-ej-nombre">{ejercicio.nombre}</h2>
          {marca ? (
            <p className="sx-ej-ultima">
              La última vez ·{" "}
              {marca.series.map((s, n) => (
                <span key={n}>
                  {n > 0 && " · "}
                  {s.peso}kg × {s.repeticiones}
                </span>
              ))}
            </p>
          ) : (
            <p className="sx-ej-ultima sx-ej-ultima-vacia">Primera vez con este ejercicio</p>
          )}
        </div>

        {ejercicio.gif && (
          <img className="sx-gif" src={ORIGEN_API + ejercicio.gif} alt="" loading="lazy" />
        )}

        {ejercicio.notas && <p className="sx-ej-notas">{ejercicio.notas}</p>}

        <div className="sx-series-cabecera">
          <h3>
            Series <span>{hechasEj}/{ejercicio.series.length}</span>
          </h3>
          <div className="sx-series-mas">
            <button type="button" onClick={() => cambiarSeries("menos")} aria-label="Quitar una serie">
              <FiMinus />
            </button>
            <button type="button" onClick={() => cambiarSeries("mas")} aria-label="Añadir una serie">
              <FiPlus />
            </button>
          </div>
        </div>

        <ul className="sx-series">
          {ejercicio.series.map((s, k) => (
            <Serie
              key={k}
              n={k + 1}
              serie={s}
              ultima={marca?.series?.[k] || marca?.series?.[marca.series.length - 1]}
              onGuardar={(cambios) => guardarSerie(k, cambios)}
            />
          ))}
        </ul>

        {error && <p className="sx-error">{error}</p>}
      </main>

      {/* Navegación entre ejercicios */}
      <nav className="sx-nav">
        <button type="button" disabled={i === 0} onClick={() => setI((v) => v - 1)}>
          <FiChevronLeft /> Anterior
        </button>
        <div className="sx-puntos">
          {log.ejercicios.map((e, k) => {
            const done = e.series.length > 0 && e.series.every((s) => s.completada);
            return (
              <button
                key={k}
                type="button"
                className={`sx-punto${k === i ? " sx-punto-on" : ""}${done ? " sx-punto-ok" : ""}`}
                onClick={() => setI(k)}
                aria-label={`Ir a ${e.nombre}`}
              />
            );
          })}
        </div>
        <button type="button" disabled={i === total - 1} onClick={() => setI((v) => v + 1)}>
          Siguiente <FiChevronRight />
        </button>
      </nav>

      {descanso !== null && <Descanso segundos={descanso} onCerrar={() => setDescanso(null)} />}
    </div>
  );
}

export default SesionEntreno;
